import Parser from 'html-react-parser';

export const truncateText = (text, maxLength) => {
    // Check if text is defined and is a string
    if (typeof text !== 'string') {
        // If text is undefined or not a string, return it as is
        return text;
    }

    // Truncate text if it exceeds maxLength, adding ellipsis if truncated
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

export const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

export const countWords = (html) => {
    // Remove HTML tags and convert HTML entities
    const text = html.replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
    return text ? text.split(/\s+/).length : 0;
};

export const getMonthYear = (dateInput) => {
    // Reuse the formatDate function to parse the input date
    const formattedDate = formatDate(dateInput);

    // Check if formatDate returned a valid date
    if (formattedDate === 'Invalid Date' || formattedDate === 'No Date Available') {
        return formattedDate;
    }

    // Extract day, month, and year from the formatted date (DD/MM/YYYY)
    const [, month, year] = formattedDate.split('/');

    // Array of month names
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Convert the month number to the corresponding month name
    const monthName = monthNames[parseInt(month, 10) - 1];

    // Return the formatted Month Name/Year
    return `${monthName} ${year}`;
};

// Update the formatDate function to include time option
export const formatDate = (dateInput, includeTime = false) => {
    // Check if the input is already a Date object
    let date;
    if (dateInput instanceof Date && !isNaN(dateInput)) {
        date = dateInput;
    } else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
    } else {
        return 'No Date Available';
    }

    // Check if the parsed date is valid
    if (!isNaN(date.getTime())) {
        // Format to DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        if (includeTime) {
            // Add time in 24-hour format (HH:mm)
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
        }
        
        return `${day}/${month}/${year}`;
    } else {
        return 'Invalid Date';
    }
};

export const formatDateSimple = (dateString) => {
    try {
        // Handle DD/MM/YYYY format
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/').map(Number);
            const date = new Date(year, month - 1, day);

            // Check for invalid date
            if (isNaN(date.getTime())) {
                console.error("Invalid date format:", dateString);
                return;
            }

            // Format the date for display (e.g., "October 18, 2024") 
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return date.toLocaleDateString('en-IE', options);
        }

        // Handle other date formats
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.error("Invalid date format:", dateString);
            return;
        }

        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-IE', options);

    } catch (error) {
        console.error("Error handling date:", error);
    }
};

export const newDate = (dateString) => {
    const date = new Date(dateString);
    return date;
}

export const removeTime = (dateString) => {
    return dateString ? dateString.split(' ')[0] : 'N/A';
}

// Utility function to parse a date in dd/mm/yyyy format
export const parseDate = (dateString) => {
    // Check if dateString is already a Date object
    if (dateString instanceof Date) {
        return dateString;
    }

    // Check if dateString is a string
    if (typeof dateString !== 'string') {
        return null;
    }

    // Split the string to extract day, month, and year
    const [day, month, year] = dateString.split('/').map(Number);

    // Validate extracted values
    if (!day || !month || !year) {
        return null;
    }

    // Create and return a new Date object
    return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
};

export const getQuarterFromDate = (date) => {
    if (!date) return null;
    const month = date.getMonth();
    if (month >= 0 && month <= 2) return 'Q1';
    if (month >= 3 && month <= 5) return 'Q2';
    if (month >= 6 && month <= 8) return 'Q3';
    if (month >= 9 && month <= 11) return 'Q4';
    return null;
};

export const removeURLs = (text) => {
    if (text == undefined) {
        return "";
    }
    // Regular expression to match URLs and surrounding brackets
    const urlPattern = /\[?https?:\/\/[^\s]+\]?/g;
    return text.replace(urlPattern, '').trim();
};

export const cleanLinks = (text) => {
    // Check if text is defined and is a string
    if (typeof text !== 'string') {
        // Return text as it is if it's undefined or not a string
        return text;
    }

    // Match URLs (starting with http/https), mailto email addresses, and relative URLs starting with /
    const linkPattern = /(\[?https?:\/\/[^\s\]]+\]?|\[?mailto:[^\s\]]+\]?|\[?\/[^\s\]]+\]?)/g;
    return text.replace(linkPattern, '');
};

export const convertLastWordToLink = (text) => {
    // Regular expression to match the last word before the URL in brackets
    const urlRegex = /(\w+)\s*\[([^\]]+)\]/g;
    // Replace the last word before the URL with an HTML anchor tag using the URL
    return text.replace(urlRegex, (match, wordBeforeUrl, url) => {
        // Return the hyperlink with the word before and the URL
        return `<a href="${url}" target="_blank">${wordBeforeUrl}</a>`;
    });
};

export const getLastSpecificDay = (dayOfWeek) => {
    const today = new Date();
    const currentDayOfWeek = today.getDay();
    const daysDifference = (currentDayOfWeek + 7 - dayOfWeek) % 7;
    const lastSpecificDay = new Date(today);
    lastSpecificDay.setDate(today.getDate() - daysDifference);
    lastSpecificDay.setHours(0, 0, 0, 0);
    return lastSpecificDay;
};

export const getToday = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set time to the end of today
    return today;
};

export const replaceCss = (text) => {
    return text.replace(/&/g, "").replace(/\s+/g, "");
};

export function addTargetBlankToLinks(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    // Find all anchor tags in the parsed HTML
    const anchorTags = doc.querySelectorAll('a');
    anchorTags.forEach(anchor => {
        // Add target="_blank" to each anchor tag
        anchor.setAttribute('target', '_blank');
        // (Optional) Add rel="noopener noreferrer" for security reasons
        anchor.setAttribute('rel', 'noopener noreferrer');
    });
    return doc.body.innerHTML;
}

export const cleanJson = (text) => {
    // Check if text is defined and is a string
    if (typeof text !== 'string') {
        // Return text as is if it's undefined or not a string
        return text;
    }
    // Perform the replacement
    return text.replace(/\\u0027/g, "'");
};

export function extractFileName(filePath) {
    // Remove any fragment identifiers (e.g., #zoom=75) if they exist
    const cleanPath = filePath.split('#')[0];
    // Split by '/' to get the last part, which should be the file name
    const fileName = cleanPath.split('/').pop();
    return fileName;
}

export function removeTitle(htmlString, title) {
    // return Parser(htmlString);
    // Escape special characters in the title for safe usage in regex
    const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Create a regex to match the title at the beginning of the content, even if there are HTML tags between words
    const titlePattern = escapedTitle.split(' ').map(word => `(?:<[^>]+>)*${word}`).join('(\\s|(?:<[^>]+>))*');
    const regex = new RegExp(`^${titlePattern}`, 'i');  // 'i' flag for case-insensitive matching
    // Check if the HTML string starts with the title (handling tags and case insensitivity) and remove it
    const cleanedHtml = htmlString.replace(regex, '').trim();
    // Return the cleaned HTML parsed into React components
    return Parser(cleanedHtml);
}

export function removeFirstBoldTag(htmlString, title) {
    // Check if title exists and has content
    if (title) {
        // Split title into words and take the first few (e.g., first 3 words for comparison)
        const titleWords = title.split(" ").slice(0, 3).join(" ");
        // Create a temporary DOM element to manipulate the HTML content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        // Extract the plain text from the start of htmlString for comparison
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        const initialText = textContent.split(" ").slice(0, 3).join(" ");
        // Check if the title's starting words match the initial words of htmlString
        if (initialText.includes(titleWords)) {
            // Find the first <b> element or any element with font-weight: bold style
            let boldElement = tempDiv.querySelector('b, span[style*="font-weight: bold"], span[style*="font-weight:bold"]');
            // Collect consecutive elements with bold styles
            while (boldElement) {
                let nextSibling = boldElement.nextSibling;
                // Remove the bold element
                boldElement.remove();
                // If the next sibling is also styled as bold, remove it as well (to handle split bold spans)
                if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.style.fontWeight === 'bold') {
                    boldElement = nextSibling;
                } else {
                    boldElement = null;
                }
            }
        }

        // Return the cleaned HTML as React components
        return Parser(tempDiv.innerHTML);
    } else {
        // If no title exists, return the original htmlString without modification
        return Parser(htmlString);
    }
}

export function normalizeText(input) {
    if (!input) return '';
    if (input === 'N/A') return 'N/A'; // Single check for 'N/A'

    // Check if the input is all in uppercase
    if (input === input.toUpperCase()) {
        // Convert to lowercase and capitalize the first letter of each word
        return input.toLowerCase().replace(/(?:^|\s)\S/g, (match) => match.toUpperCase());
    }

    // If not all caps, return the input unchanged
    return input;
}

export const formatResponse = (response) => {
    if (typeof response !== 'string') {
        console.error('Expected a string but received:', response);
        return "Error: Response is not valid.";
    }

    const formattedResponse = response
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n\n/g, '<br><br>')
        .replace(/\n/g, '<br>');

    return `<p>${formattedResponse}</p>`;
};

export function removeTitleFromPlainText(description, title) {
    // Check if description and title are defined and are strings
    if (typeof description !== 'string' || typeof title !== 'string') {
        // Return description as it is if either is undefined or not a string
        return description;
    }

    // Normalize and trim the title and description (case-insensitive and space-insensitive)
    const normalizedTitle = title.trim().toLowerCase();
    const normalizedDescription = description.trim().toLowerCase();

    // Check if the description starts with the title
    if (normalizedDescription.startsWith(normalizedTitle)) {
        // Remove the title from the description (case-sensitive version for keeping original format)
        return description.slice(title.length).trim();
    }

    // If no match, return the original description
    return description;
}

// Clean HTML content for indexing
export const cleanHtmlForIndexing = (html) => {
    if (!html) return '';

    // Create a temporary div to parse HTML
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;

    // Remove script and style elements
    const scripts = tmp.getElementsByTagName('script');
    const styles = tmp.getElementsByTagName('style');
    while (scripts.length > 0) scripts[0].remove();
    while (styles.length > 0) styles[0].remove();

    // Get text content
    let text = tmp.textContent || tmp.innerText || '';

    // Remove any remaining HTML entities
    text = text.replace(/&[a-z0-9]+;/gi, ' ');

    // Remove any metadata tags in square brackets
    text = text.replace(/\[[^\]]*\]/g, '');

    // Remove URLs
    text = text.replace(/\[?https?:\/\/[^\s]+\]?/g, '');

    // Normalize whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
};

